import { GlobeIcon } from '@phosphor-icons/react'
import { mappedColors } from '@pixee/design-system'
import * as labelStyles from '../../components/composition-bar/segments-labels.css.ts'
import { pluralize } from '../../utils/text-utils.ts'
import { TONES } from '../../utils/threat-model/exposure-tone.css.ts'
import type { InternetFacing } from '../../utils/threat-model/exposure.ts'
import { EstateCompositionBar } from './estate-composition-bar.tsx'
import type { ExposureBand } from './org-threat-posture.ts'

/**
 * Coverage and exposure in one bar: the whole estate is the width, and the modelled part of it is split by
 * what each model says about reachability.
 *
 * **One bar for two figures.** It sits under the coverage and internet-facing callouts and is the
 * denominator for both: the filled part is the estate that has a model, and its colours are the split the
 * second callout lumps together — `yes` plus `partial`, where a `partial` is a hedge ("exposure depends on
 * deployment") worth seeing apart. Drawn over the modelled count alone, it could not show how much of the
 * estate that split speaks for.
 *
 * Exposure partitions the modelled repositories — each has exactly one value — which is what makes a
 * stacked bar honest here.
 */

const EXPOSURE_NAMES: Record<InternetFacing, string> = {
  yes: 'Yes',
  partial: 'Partial',
  no: 'No',
  unknown: 'Unknown',
}

/**
 * The repositories with no model at all, held apart from the four exposure values.
 *
 * A pale neutral rather than another tone, because this segment is an absence rather than a category — it
 * should read as the unfilled end of the bar. Deliberately distinct from `TONES.unknown`: that one is a
 * model saying it could not tell, which is a different claim from there being no model to ask.
 */
const NOT_MODELLED_TONE = mappedColors.neutral.neutral70
const NOT_MODELLED_KEY = 'not-modelled'

type ExposureBarProps = {
  exposureMix: ExposureBand[]
  /** Modelled repositories — the ones the exposure split speaks for. */
  modelledCount: number
  /** Repositories with no model, so the bar's width is the whole estate rather than the modelled part. */
  unmodelledCount: number
}

export const ExposureBar: React.FC<ExposureBarProps> = ({ exposureMix, modelledCount, unmodelledCount }) => {
  const total = modelledCount + unmodelledCount

  const rows = [
    ...exposureMix.map(band => ({
      key: band.internetFacing,
      name: EXPOSURE_NAMES[band.internetFacing],
      count: band.repositoryCount,
      color: TONES[band.internetFacing],
    })),
    // Last, so the modelled estate reads as one run of colour and the shortfall as the tail. Dropped
    // entirely when the estate is fully modelled, rather than drawn as a zero-width segment.
    { key: NOT_MODELLED_KEY, name: 'Not modelled', count: unmodelledCount, color: NOT_MODELLED_TONE },
  ].filter(row => row.key !== NOT_MODELLED_KEY || row.count > 0)

  return (
    <EstateCompositionBar
      rows={rows}
      total={modelledCount === 0 ? 0 : total}
      heading="Exposure: Internet-facing"
      ariaPrefix="Exposure across the estate."
      icon={<GlobeIcon className={labelStyles.leadingIconSvg} weight="light" />}
      note={`All ${total} ${pluralize(total, 'repository', 'repositories')}, split by what each model says about reachability from an untrusted network.`}
      emptyMessage="No repository has a threat model yet, so there is nothing to split."
    />
  )
}
